
export const onboarding = {
  title: "Create Your Creator Page",
  navigation: {
    back: "Back",
    next: "Next",
    finish: "Finish"
  },
  toast: {
    name_required: "Name Required",
    name_required_desc: "Please enter your creator name before continuing.",
    thank_you: "Thank You!",
    thank_you_desc: "Your onboarding information has been submitted.",
    saved: "Profile Saved",
    saved_desc: "Your creator profile has been successfully created.",
    error: "Error",
    error_desc: "There was an error saving your profile. Please try again."
  },
  steps: {
    basic_info: {
      title: "Basic Information",
      description: "Let's start with some essential information about you as a creator.",
      tip_title: "Pro tip",
      tip_description: "A good profile picture and description builds trust with your followers. Categories help find the right audience.",
      personal_data: "Personal data",
      creator_name: "Creator name",
      creator_name_placeholder: "Enter your creator name",
      main_keywords: "Main keywords (minimum 2)",
      keywords_help: "Choose words that best describe you",
      keywords_selected: "keywords selected",
      keywords_minimum: "(minimum 2 required)",
      keywords_placeholder: "Choose words that describe you best...",
      keywords_list: [
        "sustainable", "exclusive", "technological", "creative", "innovative", "authentic",
        "inspiring", "motivating", "educational", "entertaining", "practical", "elegant",
        "modern", "traditional", "expert", "friendly", "energetic", "calm",
        "colorful", "minimalist", "luxury", "affordable", "unique", "community",
        "personal", "professional", "family-friendly", "youthful", "experienced", "trendy"
      ],
      profile_picture: "Profile picture",
      upload_image: "Upload image",
      categories_placeholder: "Choose your areas from the categories below...",
      category_label: "Your areas",
      categories_selected: "selected",
      selected_categories: "Selected areas",
      categories_summary: "Great! You selected {count} areas. Feel free to choose more if relevant to your content!",
      categories: {
        chef: "Chef / culinary expert",
        fitness: "Fitness trainer",
        travel_blogger: "Travel blogger",
        photographer: "Photographer",
        beauty_expert: "Beauty expert",
        fashion_stylist: "Fashion designer / stylist",
        gamer: "Gamer / streamer",
        tech_expert: "Tech expert",
        teacher: "Teacher / educator",
        musician: "Musician / performer",
        artist: "Artist / graphic designer",
        comedian: "Comedian / performer",
        lifestyle: "Lifestyle coach",
        business: "Business consultant",
        health: "Health expert",
        podcaster: "Podcaster",
        influencer: "Influencer",
        writer: "Writer / journalist",
        actor: "Actor",
        other: "Other"
      }
    },
    social_media: {
      title: "Social Media Links",
      description: "Connect your social media accounts to your creator profile.",
      tip_title: "Pro tip",
      tip_description: "Social media links help your fans find and follow you on all platforms. These are all optional - add the ones you use.",
      no_platforms: "No connected platforms yet",
      platforms_connected: "platforms connected",
      connected_platforms: "Connected platforms",
      connect_platform: "connection",
      username_or_profile: "Username or profile",
      website_url: "Website URL",
      save: "Save",
      delete: "Delete",
      youtube_placeholder: "https://youtube.com/c/channelname",
      username_placeholder: "@username",
      platforms: {
        instagram: {
          name: "Instagram",
          placeholder: "@username"
        },
        youtube: {
          name: "YouTube",
          placeholder: "Channel name or URL"
        },
        tiktok: {
          name: "TikTok",
          placeholder: "@username"
        },
        twitter: {
          name: "Twitter/X",
          placeholder: "@username"
        },
        facebook: {
          name: "Facebook",
          placeholder: "Page name"
        },
        linkedin: {
          name: "LinkedIn",
          placeholder: "Profile name"
        }
      }
    },
    audience: {
      title: "Target Audience",
      description: "Help us understand who your content is for.",
      tip_title: "Why this matters?",
      tip_description: "Understanding your audience helps personalize your content and communication. You can choose multiple age groups and styles.",
      primary_age_group: "Primary age group",
      selected: "selected",
      communication_style: "How do you prefer to address your audience?",
      summary_title: "Your target audience",
      age_groups: "Age groups:",
      age_group: "Age group",
      communication_styles: "Communication style:",
      address_style_question: "How would you like to communicate with your followers?",
      age_options: {
        "all-ages": {
          label: "All ages",
          description: "Content relevant for all age groups"
        },
        "13-17": {
          label: "13-17 years",
          description: "High school students, young teenagers"
        },
        "18-24": {
          label: "18-24 years",
          description: "College students, early career builders"
        },
        "25-34": {
          label: "25-34 years",
          description: "Young adults, career professionals"
        },
        "35-44": {
          label: "35-44 years",
          description: "Family starters, experienced professionals"
        },
        "45+": {
          label: "45+ years",
          description: "Experienced adults, mentors"
        }
      },
      communication_options: {
        casual: {
          label: "Casual & friendly",
          description: "Relaxed, like talking to a good friend",
          example: "\"Hey! Today I'll show you...\""
        },
        professional: {
          label: "Professional & formal",
          description: "Professional, respectful tone",
          example: "\"I would like to present to you...\""
        },
        motivational: {
          label: "Motivational & inspiring",
          description: "Enthusiastic, with positive energy",
          example: "\"You can do it too! Let's start...\""
        },
        humorous: {
          label: "Humorous & entertaining",
          description: "Fun, with lots of laughter",
          example: "\"Well, this is going to be fun...\""
        }
      },
      address_styles: {
        casual: "Casual & friendly",
        professional: "Professional & formal",
        motivational: "Motivational & inspiring",
        humorous: "Humorous & entertaining"
      },
      examples: {
        "all-ages": ["All ages", "General content", "Wide audience"],
        "13-17": ["TikTok content", "Gaming", "Music"],
        "18-24": ["Lifestyle", "Learning tips", "Career advice"],
        "25-34": ["Professional development", "Wellness", "Relationships"],
        "35-44": ["Parenting advice", "Home", "Health"],
        "45+": ["Wisdom", "Hobbies", "Retirement planning"]
      },
      example_label: "Example:"
    },
    services: {
      title: "Your Services",
      description: "Select which services you want to offer to your audience.",
      tip_title: "Pro tip",
      tip_description: "Start with 2-3 services you can definitely deliver. You can expand your offerings later. Mixed price categories help reach different buyers.",
      ai_recommendation: "AI recommendation based on your categories",
      ai_description: "Based on your selected areas, we recommend these services:",
      select_services: "Choose some services",
      services_selected: "services selected",
      selected_services: "Your selected services",
      potential_revenue: "Potential revenue",
      examples: "Examples:",
      services: "Services",
      help_text: "Select the services you'd like to offer.",
      personalVideos: "Personal video messages",
      personalVideosDesc: "Custom videos for fans",
      privateMessages: "Private messages",
      privateMessagesDesc: "Individual advice in writing",
      digitalContent: "Digital content",
      digitalContentDesc: "E-books, guides",
      oneOnOneConsultation: "One-on-one consultation",
      oneOnOneConsultationDesc: "Personal video call",
      groupConsultation: "Group events",
      groupConsultationDesc: "Group events",
      subscriptionContent: "Subscription content",
      subscriptionContentDesc: "Regular exclusive content",
      selected: "Selected Services",
      selectedHelp: "These are the services you'll be able to offer to your fans.",
      service_types: {
        personalVideos: {
          title: "Personal video messages",
          shortDesc: "Custom videos for fans",
          description: "Send personalized video messages to your followers for special occasions",
          price: "$20 - $100",
          time: "5-15 minutes",
          examples: ["Birthday greetings", "Motivational message", "Advice"]
        },
        privateMessages: {
          title: "Private messages",
          shortDesc: "Individual advice in writing",
          description: "Personal text messages and advice for your followers",
          price: "$5 - $25",
          time: "Instant",
          examples: ["Quick advice", "Question answering", "Motivation"]
        },
        digitalContent: {
          title: "Digital content",
          shortDesc: "E-books, guides",
          description: "E-books, guides, templates and other downloadable content",
          price: "$10 - $75",
          time: "Instant download",
          examples: ["Template packages", "Workout plans", "Preset packages"]
        },
        oneOnOneConsultation: {
          title: "One-on-one consultation",
          shortDesc: "Personal video call",
          description: "Personal video calls for deeper advice and coaching",
          price: "$50 - $250",
          time: "30-60 minutes",
          examples: ["Coaching session", "Professional advice", "Mentoring"]
        },
        groupConsultation: {
          title: "Group events",
          shortDesc: "Group events",
          description: "Online and offline group workshops",
          price: "$15 - $75/person",
          time: "60-90 minutes",
          examples: ["Workshop", "Q&A session", "Community event"]
        },
        subscriptionContent: {
          title: "Subscription content",
          shortDesc: "Regular exclusive content",
          description: "Monthly or weekly exclusive content on a subscription basis",
          price: "$10 - $50/month",
          time: "Ongoing",
          examples: ["Weekly tips", "Exclusive videos", "Early access"]
        }
      }
    },
    design: {
      title: "Design preferences",
      description: "Let us know your preferred visual style for your creator page.",
      tip_title: "Pro tip",
      tip_description: "Good design builds trust and helps you stand out from competitors. Choose a style that reflects your personality and attracts your target audience.",
      choose_style: "Choose design style",
      selected: "Selected",
      color_palette: "Color palette selection",
      font_selection: "Font selection",
      inspiration_images: "Inspiration images (optional)",
      design_inspiration_note: "💡 These are just inspiration examples - your page will be created with a completely unique design!",
      select_images: "Choose images that inspire you",
      upload_own: "Upload your own image",
      design_styles: {
        minimal: {
          title: "Minimal",
          description: "Clean, simple design with few elements"
        },
        modern: {
          title: "Modern",
          description: "Contemporary design with vibrant colors"
        },
        vibrant: {
          title: "Vibrant",
          description: "Eye-catching colors and gradients"
        },
        elegant: {
          title: "Elegant",
          description: "Sophisticated, premium appearance"
        },
        natural: {
          title: "Natural",
          description: "Natural colors and forms"
        },
        playful: {
          title: "Playful",
          description: "Fun, creative appearance"
        }
      },
      font_categories: {
        sans_serif: "Sans-serif",
        serif: "Serif",
        display: "Display"
      },
      color_palettes: {
        "natural-green": "Natural Greens",
        "scandinavian-light": "Scandinavian Light",
        "modern-tech-blue": "Modern Tech Blue",
        "pastel-cheerful": "Pastel Cheerful",
        "dark-elegance": "Dark Elegance",
        "bohemian-purple": "Bohemian Purple",
        "warm-sunset": "Warm Sunset",
        "minimal-beige": "Minimal Beige",
        "nordic-cool": "Nordic Cool",
        "refreshing-spring": "Refreshing Spring",
        "mature-autumn": "Mature Autumn",
        "creative-momentum": "Creative Momentum",
        "dark-theme": "Dark Theme",
        "delicate-pastel": "Delicate Pastel",
        "luxury-gold": "Luxury Gold",
        "retro": "Retro",
        "scandinavian-stone": "Scandinavian Stone",
        "romantic": "Romantic",
        "coastal": "Coastal",
        "dark-green": "Dark Green"
      },
      font_options: {
        inter: { name: "Inter", description: "Modern and clean", sample: "Life is beautiful, let's live it!" },
        poppins: { name: "Poppins", description: "Friendly and easy to read", sample: "Creativity for every day" },
        montserrat: { name: "Montserrat", description: "Elegant and professional", sample: "Secrets of successful business" },
        playfair: { name: "Playfair Display", description: "Classic and refined", sample: "Art and beauty" },
        merriweather: { name: "Merriweather", description: "Readable and traditional", sample: "Stories and experiences" },
        oswald: { name: "Oswald", description: "Strong and attention-grabbing", sample: "BOLD MESSAGE" },
        manrope: { name: "Manrope", description: "Harmonious and modern", sample: "Balance and harmony" },
        bangers: { name: "Bangers", description: "Playful and energetic", sample: "FUN & CREATIVE!" }
      },
      design_elements: {
        minimal: ["simple line", "empty space"],
        modern: ["neon accent", "geometric shapes"],
        vibrant: ["colorful spots", "creative chaos"],
        elegant: ["gold decoration", "symmetry"],
        natural: ["organic form", "natural texture"],
        playful: ["cheerful icons", "bubble shapes"]
      },
      style_options: [
        "Minimalist white", "Modern gradient", "Organic, natural", "Premium, dark elegance",
        "Playful, illustrated", "Retro/vintage", "Artistic, creative", "Monochrome (black-white)", 
        "Futuristic", "Pastel, soft", "Urban / Street", "Photo-based, magazine"
      ],
      upload_logo: "Upload own logo (optional)",
      upload_logo_description: "Upload your own logo to personalize your page.",
      upload_logo_button: "Upload logo",
      upload_logo_formats: "PNG, JPG, SVG formats",
      font_selection_title: "Font selection",
      font_selection_description: "The font has a big impact on the mood and readability of the page.",
      inspiration_gallery: "Inspiration gallery",
      inspiration_optional: "(optional)",
      inspiration_description: "Choose mood images that you like or reflect your style. These help understand your visual preferences and mood.",
      upload_images: "Upload your own images",
      upload_images_description: "Click or drag images here",
      images_selected: "images selected",
      preview_title: "Page preview",
      preview_design_style: "Design style:",
      preview_color_palette: "Color palette:",
      preview_font: "Font:",
      preview_inspiration: "Inspiration:",
      preview_images: "images",
      not_selected: "Not selected"
    }
  },
  ai_assistant: {
    title: "Personalize your AI assistant",
    description: "Give your AI assistant a name and choose an avatar to make it unique on your page.",
    fields: {
      name: {
        label: "AI assistant name",
        placeholder: "e.g. Anna, Peter, Alex...",
        description: "This name will appear when the AI responds to your visitors"
      },
      avatar: {
        label: "Choose avatar",
        description: "Select an avatar that represents your AI assistant", 
        select_placeholder: "Choose AI assistant personality...",
        pick_your_vibe: "Pick Your Vibe!",
        selected_text: "Perfect choice! This is how your AI assistant will look 💫"
      }
    },
    avatars: {
      fairy_sparkle: "Sparkle Fairy",
      dragon_buddy: "Dragon Buddy",
      unicorn_magic: "Magic Unicorn",
      phoenix_flame: "Flame Phoenix",
      griffin_golden: "Golden Griffin",
      pegasus_cloud: "Cloud Pegasus",
      owl_wise: "Wise Owl",
      fox_magic: "Magic Fox",
      cat_fluffy: "Fluffy Cat",
      rabbit_flower: "Flower Rabbit",
      bear_honey: "Honey Bear",
      dolphin_splash: "Splash Dolphin"
    }
  }
};
